import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import './CataloguePage.css';

const API_BASE = 'https://quqex11w8g.execute-api.ap-south-1.amazonaws.com/prod';

export default function CataloguePage() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const tableLabel = searchParams.get('table') || null;
    const queueParam = searchParams.get('queue') || null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [selectedItem, setSelectedItem] = useState(null);
    const [vegFilter, setVegFilter] = useState(false);

    const [cart, setCart] = useState({});
    const [cartOpen, setCartOpen] = useState(false);
    const [ordering, setOrdering] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderId, setOrderId] = useState(null);
    const [tokenNumber, setTokenNumber] = useState(null);
    const [orderNote, setOrderNote] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [customerPhone, setCustomerPhone] = useState(() => localStorage.getItem('qkard_customer_phone') || '');

    // Token tracking
    const [activeToken, setActiveToken] = useState(null);
    const [tokenStatus, setTokenStatus] = useState(null);

    // Fetch catalogue
    useEffect(() => {
        fetch(`${API_BASE}/catalogue/${slug}`)
            .then(r => { if (!r.ok) throw new Error(r.status === 404 ? 'not_found' : 'error'); return r.json(); })
            .then(d => { setData(d); setLoading(false); })
            .catch(e => { setError(e.message); setLoading(false); });
    }, [slug]);

    // Load active token from localStorage
    useEffect(() => {
        const saved = localStorage.getItem(`qkard_token_${slug}`);
        if (saved) {
            try {
                const token = JSON.parse(saved);
                setActiveToken(token);
            } catch { }
        }
    }, [slug]);

    // Poll token status every 10s
    useEffect(() => {
        if (!activeToken || !data?.business) return;
        const businessId = data.business.businessId;

        async function checkStatus() {
            try {
                const res = await fetch(`${API_BASE}/orders?businessId=${businessId}&mode=live`);
                const resData = await res.json();
                const orders = resData.orders || [];
                const myOrder = orders.find(o => o.tokenNumber === activeToken.tokenNumber);

                if (!myOrder || myOrder.status === "billed" || myOrder.status === "done" || myOrder.status === "cancelled") {
                    // Order complete � show message then clear after 5s
                    setTokenStatus({ status: "complete" });
                    setTimeout(() => { localStorage.removeItem(`qkard_token_${slug}`); setActiveToken(null); setTokenStatus(null); }, 5000);


                }

                const activeOrders = orders.filter(o => o.status === 'pending' || o.status === 'preparing');
                const myIndex = activeOrders.findIndex(o => o.tokenNumber === activeToken.tokenNumber);
                const nowServing = orders.find(o => o.status === 'preparing')?.tokenNumber
                    || orders.find(o => o.status === 'pending')?.tokenNumber
                    || null;

                setTokenStatus({
                    status: myOrder.status,
                    ordersAhead: myIndex > 0 ? myIndex : 0,
                    nowServing,
                });
            } catch { }
        }

        checkStatus();
        const interval = setInterval(checkStatus, 10000);
        return () => clearInterval(interval);
    }, [activeToken, data, slug]);

    const visibleItems = data?.items?.filter(i => {
        if (i.isVisible === false) return false;
        if (vegFilter && i.foodType !== 'veg') return false;
        return true;
    }) ?? [];
    const categories = data ? ['All', ...new Set(visibleItems.map(i => i.category).filter(Boolean))] : [];
    const filteredItems = visibleItems.filter(i => activeCategory === 'All' || i.category === activeCategory);
    const grouped = categories.slice(1).map(cat => ({ cat, items: filteredItems.filter(i => i.category === cat) })).filter(g => g.items.length > 0);

    const cartCount = Object.values(cart).reduce((a, b) => a + b, 0);
    const cartTotal = Object.entries(cart).reduce((total, [itemId, qty]) => {
        const item = visibleItems.find(i => i.itemId === itemId);
        return total + (item?.price || 0) * qty;
    }, 0);
    const cartItems = Object.entries(cart).filter(([, qty]) => qty > 0).map(([itemId, qty]) => {
        const item = visibleItems.find(i => i.itemId === itemId);
        return { ...item, qty };
    });

    function addToCart(itemId) { setCart(prev => ({ ...prev, [itemId]: (prev[itemId] || 0) + 1 })); }
    function removeFromCart(itemId) {
        setCart(prev => { const next = { ...prev }; if ((next[itemId] || 0) <= 1) delete next[itemId]; else next[itemId]--; return next; });
    }
    const getDeviceId = async () => {
    try {
        const FingerprintJS = await import('@fingerprintjs/fingerprintjs');
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        return result.visitorId;
    } catch {
        // Fallback to localStorage ID
        let id = localStorage.getItem('qkard_device_id');
        if (!id) {
            id = 'dev-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11);
            localStorage.setItem('qkard_device_id', id);
        }
        return id;
    }
};

    function clearCart() { setCart({}); setCartOpen(false); setOrderNote(''); setDeliveryAddress(''); }

    async function handlePlaceOrder() {
        if (cartItems.length === 0) return;
        if (!customerPhone.trim() || customerPhone.trim().length < 10) {
            alert('Please enter your phone number before placing order.');
            return;
        }
        localStorage.setItem('qkard_customer_phone', customerPhone.trim());
        const deviceId = await getDeviceId();
        setOrdering(true);
        try {
            const res = await fetch(`${API_BASE}/orders`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    businessId: data.business.businessId,
                    catalogueId: data.catalogue.catalogueId,
                    tableLabel: tableLabel || '',
                    items: cartItems.map(i => ({ itemId: i.itemId, name: i.name, price: i.price, qty: i.qty, unit: i.unit || '' })),
                    customerNote: orderNote,
                    deliveryAddress: deliveryAddress || '',
                    customerPhone: customerPhone.trim(),
                    deviceId,
                }),
            });
            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || 'Order failed');
            }
            const resData = await res.json();
            const token = resData.tokenNumber || null;
            setOrderId(resData.orderId);
            setTokenNumber(token);
            setOrderSuccess(true);
            clearCart();
            if (token) {
                const tokenData = { tokenNumber: token, orderId: resData.orderId, savedAt: new Date().toISOString() };
                localStorage.setItem(`qkard_token_${slug}`, JSON.stringify(tokenData));
                setActiveToken(tokenData);
            }
        } catch (e) { alert(e.message || 'Could not place order. Please try again.'); }
        finally { setOrdering(false); }
    }

    function clearActiveToken() {
        localStorage.removeItem(`qkard_token_${slug}`);
        setActiveToken(null);
        setTokenStatus(null);
    }

    if (loading) return <LoadingSkeleton />;
    if (error === 'not_found') return <NotFound />;
    if (error) return <ErrorState />;

    const { catalogue, business } = data;
    const themeColors = getThemeColors(catalogue, business);
    const accent = themeColors.accent;
    const theme = '';
    const features = catalogue.features || {};
    const hasDelivery = features.hasDelivery === true;
    const hasQueue = features.hasQueue === true;

    return (
        <div className={`qk-page ${theme}`} style={{ background: themeColors.pageBg, minHeight: "100vh" }}>

            {/* Order success */}
            {orderSuccess && (
                <div className="qk-overlay" onClick={() => { setOrderSuccess(false); setOrderId(null); setTokenNumber(null); }}>
                    <div className="qk-order-confirm" onClick={e => e.stopPropagation()}>
                        <div className="qk-confirm-icon">✅</div>
                        <div className="qk-confirm-title">{hasQueue ? "You're in the Queue! 🎫" : "Order Placed!"}</div>
                        <div className="qk-confirm-subtitle">{hasQueue ? "Show your token at the counter. Your items are ready to pack when you arrive." : ""}</div>
                        <div className="qk-confirm-subtitle" style={{ display: hasQueue ? "none" : undefined }}>
                            {tableLabel ? `Table ${tableLabel} · ` : ''}Your order has been placed successfully
                        </div>
                        {tokenNumber && (
                            <div style={{ margin: '16px 0 8px' }}>
                                <div style={{ fontSize: 13, color: '#888', marginBottom: 4 }}>Your Token</div>
                                <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: 4, color: accent, fontFamily: 'monospace' }}>
                                    {tokenNumber ? "#" + String(tokenNumber).replace("TK-", "") : ""}
                                </div>
                                <div style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>
                                    Show this to the counter when called
                                </div>
                            </div>
                        )}
                        {orderId && <div className="qk-confirm-id" style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Order #{orderId.slice(-6).toUpperCase()}</div>}
                        <button className="qk-confirm-btn" style={{ background: accent }}
                            onClick={() => { setOrderSuccess(false); setOrderId(null); setTokenNumber(null); }}>
                            Back to Menu
                        </button>
                    </div>
                </div>
            )}

            {/* Item popup */}
            {selectedItem && (
                <ItemPopup item={selectedItem} accent={accent}
                    qty={cart[selectedItem.itemId] || 0}
                    onAdd={() => addToCart(selectedItem.itemId)}
                    onRemove={() => removeFromCart(selectedItem.itemId)}
                    onClose={() => setSelectedItem(null)}
                    catalogue={catalogue} tableLabel={tableLabel} />
            )}

            {/* Cover */}
            <div className="qk-cover" style={catalogue.coverPhoto
                ? { backgroundImage: `url(${catalogue.coverPhoto})` }
                : { background: `linear-gradient(135deg, ${themeColors.header} 0%, ${accent} 100%)` }}>
                <div className="qk-cover-overlay" />
                <div className="qk-cover-content">
                    <div className="qk-avatar">
                        {catalogue.logo ? <img src={catalogue.logo} alt={catalogue.name} /> : <span style={{ color: accent }}>{initials(catalogue.name)}</span>}
                    </div>
                    <div className="qk-shop-name">{catalogue.name}</div>
                    <div className="qk-shop-meta">
                        {business.city} · {catalogue.vertical || business.category}
                        {tableLabel && <span className="qk-table-badge">Table {tableLabel}</span>}
                    </div>
                </div>
            </div>

            {/* Active token status bar */}
            {activeToken && (
                <div style={{
                    background: tokenStatus?.status === 'done' || tokenStatus?.status === 'billed' || tokenStatus?.status === 'complete'
                        ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                        : tokenStatus?.status === 'preparing'
                            ? 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)'
                            : accent,
                    color: '#fff',
                    padding: '14px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                }}>
                    <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: '8px 16px', flexShrink: 0 }}>
                        <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 2, letterSpacing: 1, textTransform: 'uppercase' }}>Token</div>
                        <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 3, fontFamily: 'monospace', lineHeight: 1 }}>
                            {"…" + String(activeToken.tokenNumber || "").replace("TK-", "")}
                        </div>
                    </div>
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        {!tokenStatus ? (
                            <div style={{ fontSize: 13, opacity: 0.85 }}>Checking status…</div>
                        ) : tokenStatus.status === 'complete' || tokenStatus.status === 'done' || tokenStatus.status === 'billed' ? (
                            <div><div style={{ fontSize: 22 }}>✅</div><div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>Order Complete!</div></div>
                        ) : tokenStatus.status === 'preparing' ? (
                            <div><div style={{ fontSize: 22 }}>🔔</div><div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>Being prepared!</div></div>
                        ) : tokenStatus.ordersAhead === 0 ? (
                            <div><div style={{ fontSize: 22 }}>⏳</div><div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>You're next!</div></div>
                        ) : (
                            <div>
                                <div style={{ fontSize: 20, fontWeight: 900, lineHeight: 1 }}>{tokenStatus.ordersAhead}</div>
                                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{tokenStatus.ordersAhead === 1 ? 'order ahead' : 'orders ahead'}</div>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', boxShadow: '0 0 0 3px rgba(255,255,255,0.3)' }} />
                        <div style={{ fontSize: 9, opacity: 0.7, letterSpacing: 1 }}>LIVE</div>
                    </div>
                </div>
            )}
            {/* Info bar */}
            <div className="qk-info-bar">
                {catalogue.businessHours && (<><div className="qk-info-pill"><div className={`qk-dot ${isOpen(catalogue.businessHours) ? 'green' : 'red'}`} /><span>{isOpen(catalogue.businessHours) ? 'Open now' : 'Closed'}</span></div><div className="qk-divider" /></>)}
                {catalogue.description && <div className="qk-info-pill qk-desc-pill">{catalogue.description}</div>}
                <button
                    className={`qk-veg-filter ${vegFilter ? 'active' : ''}`}
                    onClick={() => setVegFilter(v => !v)}
                    title="Show veg items only"
                >
                    <span style={{ width: 10, height: 10, borderRadius: 2, border: `1.5px solid ${vegFilter ? '#22c55e' : '#aaa'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: vegFilter ? '#22c55e' : '#aaa', display: 'block' }} />
                    </span>
                    <span style={{ color: vegFilter ? '#22c55e' : '#888', fontSize: 11, fontWeight: 600 }}>VEG</span>
                </button>
            </div>

            {/* Book Appointment button — only for hasBooking shops */}
            {features.hasBooking && (
                <div style={{ padding: '12px 16px 4px' }}>
                    <button
                        onClick={() => window.location.href = `/book/${catalogue.slug}`}
                        style={{
                            width: '100%', padding: '14px',
                            borderRadius: 12, border: 'none',
                            background: accent, color: '#fff',
                            fontSize: 15, fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}
                    >
                        📅 Book Appointment
                    </button>
                </div>
            )}

            {/* Category pills */}
            {categories.length > 1 && (
                <div className="qk-categories">
                    {categories.map(cat => (
                        <button key={cat} className={`qk-cat-pill ${activeCategory === cat ? 'active' : ''}`}
                            style={activeCategory === cat ? { background: accent, borderColor: accent } : {}}
                            onClick={() => setActiveCategory(cat)}>{cat}</button>
                    ))}
                </div>
            )}

            {/* Items */}
            <div className="qk-items-wrapper">
                {activeCategory === 'All' ? grouped.map(({ cat, items }) => (
                    <div key={cat}>
                        <div className="qk-section-header">{cat}</div>
                        <div className="qk-items-grid">
                            {items.map(item => <ItemCard key={item.itemId} item={item} accent={accent} qty={cart[item.itemId] || 0} onAdd={() => addToCart(item.itemId)} onRemove={() => removeFromCart(item.itemId)} onTap={() => setSelectedItem(item)} />)}
                        </div>
                    </div>
                )) : (<><div className="qk-section-header">{activeCategory}</div><div className="qk-items-grid">{filteredItems.map(item => <ItemCard key={item.itemId} item={item} accent={accent} qty={cart[item.itemId] || 0} onAdd={() => addToCart(item.itemId)} onRemove={() => removeFromCart(item.itemId)} onTap={() => setSelectedItem(item)} />)}</div></>)}
                {filteredItems.length === 0 && <div className="qk-empty">No items in this category yet</div>}
            </div>

            {catalogue.plan === 'free' && <div className="qk-powered">Powered by <span style={{ color: accent }}>Qkard</span> · qkard.app</div>}

            {/* Cart bar */}
            {cartCount > 0 && (
                <div className="qk-bottom-bar">
                    <button className="qk-cart-btn" style={{ background: accent }} onClick={() => setCartOpen(true)}>
                        <span className="qk-cart-count">{cartCount}</span>
                        <span>View Cart</span>
                        <span className="qk-cart-total">₹{cartTotal.toLocaleString('en-IN')}</span>
                    </button>
                </div>
            )}

            {/* Cart drawer */}
            {cartOpen && (
                <div className="qk-overlay" onClick={e => { if (e.target === e.currentTarget) setCartOpen(false); }}>
                    <div className="qk-drawer">
                        <div className="qk-drawer-handle" />
                        <div className="qk-drawer-header">
                            <span className="qk-drawer-title">Your Order</span>
                            {tableLabel && <span className="qk-drawer-table">Table {tableLabel}</span>}
                            <button className="qk-drawer-close" onClick={() => setCartOpen(false)}>✕</button>
                        </div>
                        <div className="qk-drawer-items">
                            {cartItems.map(item => (
                                <div key={item.itemId} className="qk-cart-item">
                                    <div className="qk-cart-item-info">
                                        <div className="qk-cart-item-name">{item.name}</div>
                                        <div className="qk-cart-item-price">₹{(item.price * item.qty).toLocaleString('en-IN')}{item.unit && <span className="qk-cart-unit"> ({item.unit})</span>}</div>
                                    </div>
                                    <div className="qk-qty-control" style={{ borderColor: accent }}>
                                        <button style={{ color: accent }} onClick={() => removeFromCart(item.itemId)}>−</button>
                                        <span style={{ color: accent }}>{item.qty}</span>
                                        <button style={{ color: accent }} onClick={() => addToCart(item.itemId)}>+</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="qk-drawer-note">
                            <input type="tel" placeholder="📱 Your phone number *"
                                value={customerPhone}
                                onChange={e => setCustomerPhone(e.target.value)}
                                className="qk-note-input"
                                maxLength={15}
                                style={{ borderColor: customerPhone.length >= 10 ? '#22c55e' : undefined }}
                            />
                        </div>
                        <div className="qk-drawer-note">
                            <input type="text" placeholder="Add a note for the shop owner..."
                                value={orderNote} onChange={e => setOrderNote(e.target.value)}
                                className="qk-note-input" />
                        </div>
                        {hasDelivery && (
                            <div className="qk-drawer-note">
                                <input type="text" placeholder="🚚 Delivery address (area, street, flat no.)"
                                    value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)}
                                    className="qk-note-input" />
                            </div>
                        )}
                        {hasQueue && (
                            <div style={{ background: "#eff6ff", borderRadius: 10, padding: "10px 14px", marginBottom: 10, fontSize: 12, color: "#1e40af", fontWeight: 600, display: "flex", alignItems: "flex-start", gap: 8 }}>
                                <span style={{ flexShrink: 0 }}>🎫</span>
                                <span>You'll get a token number. Staff will prepare your order before you reach the counter.</span>
                            </div>
                        )}
                        <div className="qk-drawer-total"><span>Total</span><span>₹{cartTotal.toLocaleString('en-IN')}</span></div>
                        <button className="qk-place-order-btn" style={{ background: accent }} onClick={handlePlaceOrder} disabled={ordering}>{ordering ? 'Joining queue...' : (hasQueue || queueParam) ? '🎫 Join Queue & Order' : 'Place Order'}</button>
                        {catalogue.whatsappNumber && (
                            <button className="qk-wa-secondary-btn" onClick={() => {
                                const num = catalogue.whatsappNumber.replace(/\D/g, '');
                                const itemLines = cartItems.map(i => `• ${i.name} x${i.qty} — ₹${i.price * i.qty}`).join('\n');
                                window.open(`https://wa.me/${num}?text=${encodeURIComponent(`Hi! I'd like to place an order${tableLabel ? ` (Table ${tableLabel})` : ''}:\n\n${itemLines}\n\nTotal: ₹${cartTotal}${orderNote ? `\nNote: ${orderNote}` : ''}`)}`, '_blank');
                            }}><WhatsAppIcon />Also send on WhatsApp</button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function ItemCard({ item, accent, qty, onAdd, onRemove, onTap }) {
    const outOfStock = item.inStock === false || item.outOfStock === true;
    const hasPhoto = item.photoUrl || item.photo;
    return (
        <div className={`qk-item-card ${outOfStock ? 'out-of-stock' : ''}`} onClick={onTap}>
            <div className="qk-item-info">
                <div className="qk-item-badge-row">
                    {item.foodType && <FoodBadge type={item.foodType} />}
                    {item.isSpecial && <span className="qk-special-tag">⭐ Special</span>}
                    <span className="qk-cat-tag">{item.category}</span>
                </div>
                <div className="qk-item-name">{item.name}</div>
                {item.description && <div className="qk-item-desc">{item.description}</div>}
                <div className="qk-item-bottom">
                    <div className="qk-item-price">
                        {item.price > 0 ? <>₹{item.price.toLocaleString('en-IN')}{item.unit && <span className="qk-unit-tag"> / {item.unit.replace('per ', '')}</span>}</> : <span className="qk-price-request">Price on request</span>}
                    </div>
                    {!outOfStock && item.price > 0 && (
                        <div className="qk-add-control" onClick={e => e.stopPropagation()}>
                            {qty === 0
                                ? <button className="qk-add-btn" style={{ borderColor: accent, color: accent }} onClick={onAdd}>+ ADD</button>
                                : <div className="qk-qty-control" style={{ borderColor: accent }}><button style={{ color: accent }} onClick={onRemove}>−</button><span style={{ color: accent }}>{qty}</span><button style={{ color: accent }} onClick={onAdd}>+</button></div>
                            }
                        </div>
                    )}
                </div>
            </div>
            <div className="qk-item-photo">
                {hasPhoto ? <img src={item.photoUrl || item.photo} alt={item.name} loading="lazy" /> : <div className="qk-photo-placeholder" />}
                {outOfStock && <div className="qk-out-badge">Out of stock</div>}
            </div>
        </div>
    );
}

function ItemPopup({ item, accent, qty, onAdd, onRemove, onClose, catalogue, tableLabel }) {
    const outOfStock = item.inStock === false || item.outOfStock === true;
    const hasPhoto = item.photoUrl || item.photo;
    return (
        <div className="qk-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="qk-item-popup">
                <button className="qk-popup-close" onClick={onClose}>✕</button>
                {hasPhoto && <div className="qk-popup-photo"><img src={item.photoUrl || item.photo} alt={item.name} /></div>}
                <div className="qk-popup-body">
                    <div className="qk-item-badge-row" style={{ marginBottom: 10 }}>
                        {item.foodType && <FoodBadge type={item.foodType} />}
                        {item.isSpecial && <span className="qk-special-tag">⭐ Chef's Special</span>}
                        <span className="qk-cat-tag">{item.category}</span>
                    </div>
                    <div className="qk-popup-name">{item.name}</div>
                    {item.description && <div className="qk-popup-desc">{item.description}</div>}
                    <div className="qk-popup-price-row">
                        <div className="qk-popup-price" style={{ color: accent }}>
                            {item.price > 0 ? <>₹{item.price.toLocaleString('en-IN')}{item.unit && <span className="qk-popup-unit"> / {item.unit.replace('per ', '')}</span>}</> : <span className="qk-price-request">Price on request</span>}
                        </div>
                        {outOfStock && <span className="qk-popup-oos">Out of stock</span>}
                    </div>
                    {!outOfStock && item.price > 0 && (
                        <div className="qk-popup-actions">
                            {qty === 0
                                ? <button className="qk-popup-add-btn" style={{ background: accent }} onClick={onAdd}>+ Add to Cart</button>
                                : <div className="qk-popup-qty-row">
                                    <div className="qk-qty-control qk-qty-lg" style={{ borderColor: accent }}>
                                        <button style={{ color: accent }} onClick={onRemove}>−</button>
                                        <span style={{ color: accent }}>{qty}</span>
                                        <button style={{ color: accent }} onClick={onAdd}>+</button>
                                    </div>
                                    <div className="qk-popup-subtotal" style={{ color: accent }}>₹{(item.price * qty).toLocaleString('en-IN')}</div>
                                </div>
                            }
                        </div>
                    )}
                    {catalogue.whatsappNumber && (
                        <button className="qk-popup-wa-btn" onClick={() => {
                            const num = catalogue.whatsappNumber.replace(/\D/g, '');
                            window.open(`https://wa.me/${num}?text=${encodeURIComponent(`Hi! I'd like to order:\n\n*${item.name}*\nQty: 1\nPrice: ₹${item.price}${tableLabel ? `\nTable: ${tableLabel}` : ''}`)}`, '_blank');
                        }}><WhatsAppIcon />Order on WhatsApp</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function FoodBadge({ type }) {
    const colors = { veg: '#22C55E', nonveg: '#EF4444', egg: '#F59E0B' };
    const color = colors[type] || '#888';
    return <div className="qk-food-badge" style={{ borderColor: color }}><div className="qk-food-dot" style={{ background: color }} /></div>;
}

function WhatsAppIcon() {
    return <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>;
}

function LoadingSkeleton() {
    return <div className="qk-page"><div className="qk-cover qk-skeleton" style={{ height: 220 }} /><div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>{[1, 2, 3].map(i => <div key={i} className="qk-skeleton" style={{ height: 100, borderRadius: 14 }} />)}</div></div>;
}

function NotFound() {
    return <div className="qk-center-page"><div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div><h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: 8 }}>Shop not found</h2><p style={{ color: '#888', fontSize: 14 }}>This catalogue link may have changed.</p></div>;
}

function ErrorState() {
    return <div className="qk-center-page"><div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div><h2 style={{ fontFamily: 'Playfair Display, serif', marginBottom: 8 }}>Something went wrong</h2><p style={{ color: '#888', fontSize: 14 }}>Please try refreshing the page.</p></div>;
}

function initials(name = '') { return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase(); }

function isOpen(hours) {
    if (!hours) return true;
    try {
        const now = new Date();
        const day = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][now.getDay()];
        const todayHours = hours[day];
        if (!todayHours || todayHours === 'closed') return false;
        const [open, close] = todayHours.split('-').map(t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; });
        return now.getHours() * 60 + now.getMinutes() >= open && now.getHours() * 60 + now.getMinutes() < close;
    } catch { return true; }
}

const THEME_COLORS = {
    "fresh-green":  { accent: "#22c55e", bg: "#f0fdf4", pageBg: "#f6fef9", header: "#15803d", headerText: "#ffffff", catActive: "#22c55e", border: "#bbf7d0", sectionLabel: "#166534" },
    "ocean-blue":   { accent: "#2563a8", bg: "#eff6ff", pageBg: "#f0f7ff", header: "#1e40af", headerText: "#ffffff", catActive: "#2563a8", border: "#bfdbfe", sectionLabel: "#1e3a5f" },
    "saffron-gold": { accent: "#f5a623", bg: "#fffbeb", pageBg: "#fffdf0", header: "#d97706", headerText: "#ffffff", catActive: "#f5a623", border: "#fde68a", sectionLabel: "#92400e" },
    "dark-premium": { accent: "#a78bfa", bg: "#1a1a2e", pageBg: "#12121e", header: "#0f0f1a", headerText: "#e2e8f0", catActive: "#a78bfa", border: "#312e81", sectionLabel: "#c4b5fd" },
    "rose-pink":    { accent: "#ec4899", bg: "#fdf2f8", pageBg: "#fef5fb", header: "#be185d", headerText: "#ffffff", catActive: "#ec4899", border: "#fbcfe8", sectionLabel: "#831843" },
    "earthy-brown": { accent: "#92400e", bg: "#fdf8f3", pageBg: "#fef9f5", header: "#78350f", headerText: "#ffffff", catActive: "#92400e", border: "#fcd9a0", sectionLabel: "#451a03" },
};

const DEFAULT_THEME = { accent: "#22c55e", bg: "#f0fdf4", pageBg: "#f6fef9", header: "#15803d", headerText: "#ffffff", catActive: "#22c55e", border: "#bbf7d0", sectionLabel: "#166534" };

function getThemeColors(catalogue, business) {
    // 1. Use saved theme if available
    if (catalogue?.theme && THEME_COLORS[catalogue.theme]) return THEME_COLORS[catalogue.theme];
    // 2. Use accentColor to find matching theme
    if (catalogue?.accentColor) {
        const found = Object.values(THEME_COLORS).find(t => t.accent === catalogue.accentColor);
        if (found) return found;
    }
    // 3. Auto-detect from vertical/category
    const v = (catalogue?.vertical || business?.category || '').toLowerCase();
    if (v.includes('dairy') || v.includes('milk')) return THEME_COLORS['ocean-blue'];
    if (v.includes('jewellery') || v.includes('jewelry') || v.includes('gold')) return THEME_COLORS['dark-premium'];
    if (v.includes('salon') || v.includes('beauty') || v.includes('spa')) return THEME_COLORS['rose-pink'];
    if (v.includes('sweet') || v.includes('bakery') || v.includes('falooda') || v.includes('snack')) return THEME_COLORS['saffron-gold'];
    if (v.includes('tailor') || v.includes('furniture') || v.includes('wood')) return THEME_COLORS['earthy-brown'];
    return DEFAULT_THEME;
}

function getTheme(vertical = '') { return ''; }








